<template>
  <div
    role="button"
    :tabindex="enabled ? 0 : -1"
    class="play-tile play-tile--0"
    @click="emitEventTileClicked"
    @keyup.enter="emitEventTileClicked"
    :class="cssModifierClass"
    :disabled="!enabled"
  >
    <div class="play-tile__outer play-tile__outer--0" :class="`play-tile__outer--${categoryColor}`">
      <div class="play-tile__inner-bg play-tile__inner-bg--0" :class="`play-tile__inner-bg--${tilePadding ? 'padded' : 'not-padded'}`" :style="playTileInnerBgInlineStyle">
        <div class="play-tile__inner-img play-tile__inner-img--0" :style="playTileInnerImgInlineStyle">
          <span v-if="mode === boardModes.TESTING.CONSTANT" class="play-tile__inner-text play-tile__inner-text--original-index">{{ originalIndex }}</span>
          <span v-if="mode === boardModes.TESTING.CONSTANT" class="play-tile__inner-text play-tile__inner-text--prompt">{{ prompt }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import {
  PATH_TO_TILE_IMAGES,
  TILE_BACKGROUND_POSITIONS,
  TILE_BACKGROUND_SIZES,
  BOARD_MODES,
  PLAY_STATES,
  INCORRECT_CLICK_FLASH_DURATION_MS,
  COLOR_SCHEMES,
} from './../constants/globalConstants';
import getRadialGradientCss from './../helpers/getRadialGradientCss';

const userVuexModule = createNamespacedHelpers('userVuexModule');

export default {
  name: 'PlayTile',
  props: {
    answerText: String,
    backgroundColor: String,
    category: String,
    categoryColor: String,
    categoryCssModifier: String,
    clicked: Boolean,
    imageFilename: String,
    imagesFolderName: String,
    invertTileImagesInDarkMode: Boolean,
    mode: String,
    numIncorrectClicks: Number,
    originalIndex: Number,
    playState: String,
    playStateCssModifier: String,
    prompt: String,
    radialGradientHexColor: String,
    tileBackgroundColor: String,
    tileBackgroundPosition: String,
    tileBackgroundSize: String,
    tileId: String,
    tilePadding: Boolean,
    tileRadialGradientHexColor: String,
  },
  data() {
    return {
      boardModes: {},
      incorrectClickFlashing: false,
    };
  },
  watch: {
    incorrectClickFlashing() {},
    numIncorrectClicks: function() {
      this.incorrectClickFlash();
    },
  },
  computed: {
    ...userVuexModule.mapGetters(['getPrefersColorScheme']),
    imageUrl: function() {
      return `${PATH_TO_TILE_IMAGES}${this.imagesFolderName}/${this.imageFilename}`;
    },
    playTileInnerBgInlineStyle: function() {
      let backgroundColorCss;
      if (this.backgroundColor) {
        backgroundColorCss = `background-color: ${this.backgroundColor};`;
      } else if (this.tileBackgroundColor) {
        backgroundColorCss = `background-color: ${this.tileBackgroundColor};`;
      } else if (this.getPrefersColorScheme === COLOR_SCHEMES.DARK.CONSTANT) {
        backgroundColorCss = `background-color: black;`;
      } else {
        backgroundColorCss = `background-color: white;`;
      }
      let backgroundSizeCss = '';
      let backgroundImageCss = '';
      if (this.radialGradientHexColor) {
        backgroundSizeCss = `background-size: 100%;`;
        backgroundImageCss = `background-image: radial-gradient(ellipse at center, ${this.radialGradientHexColor}, ${this.radialGradientHexColor}00);`;
      } else if (this.tileRadialGradientHexColor) {
        backgroundSizeCss = `background-size: 100%;`;
        backgroundImageCss = `background-image: radial-gradient(ellipse at center, ${this.tileRadialGradientHexColor}, ${this.tileRadialGradientHexColor}00);`;
      }
      return `${backgroundColorCss} ${backgroundImageCss} ${backgroundSizeCss}`;
    },
    playTileInnerImgInlineStyle: function() {
      let backgroundImageCss = this.imageUrl ? `background-image: url(${this.imageUrl});` : '';
      let backgroundPositionCss = this.tileBackgroundPosition ? `background-position: ${TILE_BACKGROUND_POSITIONS[this.tileBackgroundPosition].CSS_VALUE};` : '';
      let backgroundSizeCss = this.tileBackgroundSize ? `background-size: ${TILE_BACKGROUND_SIZES[this.tileBackgroundSize].CSS_VALUE};` : '';
      let filterCss = this.invertTileImage ? 'filter: invert(100);' : '';
      return `${backgroundImageCss} ${backgroundSizeCss} ${backgroundPositionCss} ${filterCss}`;
    },
    cssModifierClass: function() {
      const playStateModifier = `play-tile--${this.playStateCssModifier}`;
      const clickedModifier = this.clicked ? 'play-tile--clicked' : '';
      const enabledModifier = this.enabled ? 'play-tile--enabled' : 'play-tile--disabled';
      const incorrectClickFlashingModifier = this.incorrectClickFlashing ? 'play-tile--incorrect-click-flash' : '';
      if (this.mode && this.categoryColor && this.categoryCssModifier) {
        return `play-tile--${BOARD_MODES[this.mode].CSS_MODIFIER} play-tile--${this.categoryColor} play-tile--${
          this.categoryCssModifier
        } ${playStateModifier} ${clickedModifier} ${enabledModifier} ${incorrectClickFlashingModifier}`;
      } else {
        return '';
      }
    },
    enabled: function() {
      if (!this.clicked && this.playState === PLAY_STATES.PLAYING.CONSTANT) {
        return true;
      } else {
        return false;
      }
    },
    invertTileImage: function() {
      if (this.getPrefersColorScheme === COLOR_SCHEMES.DARK.CONSTANT && this.invertTileImagesInDarkMode) {
        return true;
      } else {
        return false;
      }
    },
  },
  created() {
    this.boardModes = BOARD_MODES;
  },
  methods: {
    emitEventTileClicked: function() {
      if (!this.clicked && this.playState === PLAY_STATES.PLAYING.CONSTANT) {
        this.$emit('tileClicked', this.tileId);
      }
    },
    incorrectClickFlash: function() {
      this.incorrectClickFlashing = true;
      setTimeout(() => {
        this.incorrectClickFlashing = false;
      }, INCORRECT_CLICK_FLASH_DURATION_MS);
    },
  },
};
</script>

<style lang="scss" scoped>
$hoverBoxShadowThickness: 4px;

.play-tile {
  align-items: center;
  border-radius: var(--border-radius-l);
  border: 0;
  border: 4px solid var(--bg-color);
  color: #fff;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  overflow: hidden;
  padding: 0;
  position: relative;
  transform: scale(0.92);
  transition: scale 0.1s;

  &:hover {
    @media (hover: hover) {
      transform: scale(1);
      opacity: 1;
    }
  }

  &::after {
    background-color: var(--bg-color-inverted);
    bottom: 0;
    content: '';
    display: block;
    height: 100%;
    left: 0;
    opacity: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: 100%;
  }

  &--incorrect-click-flash {
    &::after {
      /* display: block; */
      opacity: 1;
    }
  }

  &--yellow {
    &:hover {
      @media (hover: hover) {
        box-shadow: 0px 0px 0px $hoverBoxShadowThickness var(--color-yellow);
      }
    }
    &::after {
      background-color: var(--color-yellow);
    }
  }

  &--orange {
    &:hover {
      @media (hover: hover) {
        box-shadow: 0px 0px 0px $hoverBoxShadowThickness var(--color-orange);
      }
    }
    &::after {
      background-color: var(--color-orange);
    }
  }

  &--pink {
    &:hover {
      @media (hover: hover) {
        box-shadow: 0px 0px 0px $hoverBoxShadowThickness var(--color-pink);
      }
    }
    &::after {
      background-color: var(--color-pink);
    }
  }

  &--violet {
    &:hover {
      @media (hover: hover) {
        box-shadow: 0px 0px 0px $hoverBoxShadowThickness var(--color-violet);
      }
    }
    &::after {
      background-color: var(--color-violet);
    }
  }

  &--blue {
    &:hover {
      @media (hover: hover) {
        box-shadow: 0px 0px 0px $hoverBoxShadowThickness var(--color-blue);
      }
    }
    &::after {
      background-color: var(--color-blue);
    }
  }

  &--green {
    &:hover {
      @media (hover: hover) {
        box-shadow: 0px 0px 0px $hoverBoxShadowThickness var(--color-green);
      }
    }
    &::after {
      background-color: var(--color-green);
    }
  }

  &--teal {
    &:hover {
      @media (hover: hover) {
        box-shadow: 0px 0px 0px $hoverBoxShadowThickness var(--color-teal);
      }
    }
    &::after {
      background-color: var(--color-teal);
    }
  }

  &--brown {
    &:hover {
      @media (hover: hover) {
        box-shadow: 0px 0px 0px $hoverBoxShadowThickness var(--color-brown);
      }
    }
    &::after {
      background-color: var(--color-brown);
    }
  }

  &--ready {
    /* filter: blur(var(--tile-disabled-blur));
    -webkit-filter: blur(var(--tile-disabled-blur)); */
    opacity: var(--tile-disabled-opacity);

    .play-tile__inner-bg {
      filter: blur(var(--tile-disabled-blur));
      -webkit-filter: blur(var(--tile-disabled-blur));
    }
  }

  &--counting-down {
    /* filter: blur(var(--tile-disabled-blur));
    -webkit-filter: blur(var(--tile-disabled-blur)); */
    opacity: var(--tile-disabled-opacity);

    .play-tile__inner-bg {
      filter: blur(var(--tile-disabled-blur));
      -webkit-filter: blur(var(--tile-disabled-blur));
    }
  }

  &--playing {
    filter: none;
    -webkit-filter: none;
    @media (hover: hover) {
      opacity: 0.8;
    }
    @media (hover: none) {
      opacity: 1;
    }
  }

  &--disabled {
    pointer-events: none;
  }

  &--enabled {
    pointer-events: all;
  }

  &--clicked {
    outline: 0;
    pointer-events: none;
    transform: scale(0.6);

    &:hover {
      box-shadow: none;
      transform: scale(0.6);
    }

    .play-tile__outer {
      &::before {
        opacity: 1;
      }
    }
    .play-tile__inner-bg {
      opacity: 0.15;
    }
  }

  &__outer {
    height: 100%;
    position: relative;
    width: 100%;

    &--yellow {
      &::before {
        border-color: var(--color-yellow);
      }
    }

    &--orange {
      &::before {
        border-color: var(--color-orange);
      }
    }

    &--pink {
      &::before {
        border-color: var(--color-pink);
      }
    }

    &--violet {
      &::before {
        border-color: var(--color-violet);
      }
    }

    &--blue {
      &::before {
        border-color: var(--color-blue);
      }
    }

    &--green {
      &::before {
        border-color: var(--color-green);
      }
    }

    &--teal {
      &::before {
        border-color: var(--color-teal);
      }
    }

    &--brown {
      &::before {
        border-color: var(--color-brown);
      }
    }

    &::before {
      background-position: center center;
      background-repeat: no-repeat;
      background-size: contain;
      border-bottom-style: solid;
      border-left-style: solid;
      border-width: 7px;
      content: '';
      height: 2rem;
      left: 50%;
      opacity: 0;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%) rotate(-42deg);
      transition: all 0.1s;
      width: 4rem;
      z-index: 1;
    }
  }

  &__inner-bg {
    align-items: center;
    background-color: transparent;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: var(--border-radius);
    display: flex;
    height: 100%;
    justify-content: center;
    opacity: 1;
    transition: opacity 0.1s;
    width: 100%;

    &--padded {
      padding: 8%;
    }
  }

  &__inner-img {
    align-items: center;
    background-color: transparent;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: var(--border-radius);
    display: flex;
    height: 100%;
    justify-content: center;
    opacity: 1;
    transition: opacity 0.1s;
    width: 100%;
  }

  &__inner-text {
    align-items: center;
    background-color: var(--bg-color-alpha-60);
    color: var(--text-color);
    display: flex;
    justify-content: center;
    line-height: 1;

    &--original-index {
      border-radius: 50%;
      color: var(--text-color-alpha-50);
      font-size: 1rem;
      height: 2rem;
      left: 2px;
      position: absolute;
      top: 2px;
      width: 2rem;
    }

    &--prompt {
      bottom: 0;
      left: 0;
      padding: 0.5rem;
      position: absolute;
      width: 100%;
    }
  }
}
</style>
